import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner } from '@telegram-apps/telegram-ui';
import { backButton, onBackButtonClick } from '@telegram-apps/sdk';
import { useRequestById } from '../hooks/useRequests';
import { useConfig, getItsmRequestUrl } from '../hooks';
import { hapticFeedback, openExternalLink } from '../lib/telegram';
import './RequestPage.css';

// Status configuration
const STATUS_CONFIG: Record<string, { icon: string; label: string; color: string }> = {
  assigned: { icon: '📌', label: 'Назначено', color: '#FF9F0A' },
  accepted: { icon: '✅', label: 'Принято', color: '#34C759' },
  in_progress: { icon: '⚙️', label: 'В работе', color: '#0A84FF' },
  inprogress: { icon: '⚙️', label: 'В работе', color: '#0A84FF' },
  waiting_for: { icon: '⏳', label: 'Ожидание', color: '#FFD60A' },
  waitingfor: { icon: '⏳', label: 'Ожидание', color: '#FFD60A' },
  waiting_for_customer: { icon: '⏸', label: 'Ожидание клиента', color: '#FF9F0A' },
  waitingforcustomer: { icon: '⏸', label: 'Ожидание клиента', color: '#FF9F0A' },
  completed: { icon: '✔️', label: 'Завершено', color: '#34C759' },
  cancelled: { icon: '❌', label: 'Отменено', color: '#FF453A' },
  declined: { icon: '🚫', label: 'Отклонено', color: '#FF453A' },
};

const IMPACT_CONFIG: Record<string, { icon: string; label: string; color: string }> = {
  top: { icon: '●', label: 'Критическая', color: '#FF453A' },
  high: { icon: '●', label: 'Высокая', color: '#FF9F0A' },
  medium: { icon: '●', label: 'Средняя', color: '#FFD60A' },
  low: { icon: '●', label: 'Низкая', color: '#34C759' },
  none: { icon: '○', label: 'Не указана', color: 'rgba(255,255,255,0.4)' },
};

function getStatusConfig(status: string) {
  return STATUS_CONFIG[status] || { icon: '📋', label: status, color: '#64D2FF' };
}

function getImpactConfig(impact: string | null) {
  if (!impact) return IMPACT_CONFIG.none;
  return IMPACT_CONFIG[impact] || IMPACT_CONFIG.none;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  } catch {
    return '—';
  }
}

// Info row component
function InfoRow({ label, value, icon }: { label: string; value: string | null; icon?: string }) {
  return (
    <div className="request-info-row">
      {icon && <span className="request-info-icon">{icon}</span>}
      <div className="request-info-content">
        <span className="request-info-label">{label}</span>
        <span className="request-info-value">{value || '—'}</span>
      </div>
    </div>
  );
}

export function RequestPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const requestId = parseInt(id || '0', 10);

  const { data: request, isLoading, error } = useRequestById(requestId);
  const { itsmWebUrl } = useConfig();

  // Setup back button
  useEffect(() => {
    if (backButton.show.isAvailable()) {
      backButton.show();
    }

    let removeListener: (() => void) | undefined;
    if (onBackButtonClick.isAvailable()) {
      removeListener = onBackButtonClick(() => {
        hapticFeedback('light');
        navigate(-1);
      });
    }

    return () => {
      if (backButton.hide.isAvailable()) {
        backButton.hide();
      }
      if (removeListener) {
        removeListener();
      }
    };
  }, [navigate]);

  const handleOpenInItsm = () => {
    if (!itsmWebUrl) return;
    hapticFeedback('medium');
    const itsmUrl = getItsmRequestUrl(itsmWebUrl, requestId);
    openExternalLink(itsmUrl);
  };

  const handleViewHistory = () => {
    hapticFeedback('light');
    navigate(`/request/${requestId}/history`);
  };

  if (isLoading) {
    return (
      <div className="request-loading">
        <Spinner size="l" />
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="request-error">
        <div className="request-error-icon">⚠</div>
        <h2 className="request-error-title">Не удалось загрузить</h2>
        <p className="request-error-text">Проверьте подключение и попробуйте снова</p>
        <button className="request-error-btn" onClick={() => navigate(-1)}>
          Вернуться назад
        </button>
      </div>
    );
  }

  const statusConfig = getStatusConfig(request.status);
  const impactConfig = getImpactConfig(request.impact);

  return (
    <div className="request-page">
      {/* Header */}
      <header className="request-header">
        <span className="request-id">#{request.id}</span>
        <h1 className="request-subject">{request.subject}</h1>
        <div className="request-status-row">
          <div
            className="request-status-badge"
            style={{
              background: `${statusConfig.color}15`,
              borderColor: `${statusConfig.color}30`,
              color: statusConfig.color,
            }}
          >
            <span>{statusConfig.icon}</span>
            <span>{statusConfig.label}</span>
          </div>
          <div
            className="request-urgency-badge"
            style={{ color: impactConfig.color }}
          >
            <span>{impactConfig.icon}</span>
            <span>{impactConfig.label}</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="request-content">
        {/* Category section */}
        <div className="request-section">
          <div className="request-section-header">Категория</div>
          <div className="request-section-card">
            <InfoRow label="Категория" value={request.category} icon="🏷️" />
            {request.subcategory && (
              <InfoRow label="Подкатегория" value={request.subcategory} icon="📎" />
            )}
          </div>
        </div>

        {/* People section */}
        <div className="request-section">
          <div className="request-section-header">Участники</div>
          <div className="request-section-card">
            <InfoRow label="Инициатор" value={request.requestedBy} icon="👤" />
            {request.requestedFor && request.requestedFor !== request.requestedBy && (
              <InfoRow label="Для кого" value={request.requestedFor} icon="👥" />
            )}
            <InfoRow label="Исполнитель" value={request.member} icon="🎯" />
            {request.team && (
              <InfoRow label="Команда" value={request.team} icon="👥" />
            )}
          </div>
        </div>

        {/* Dates section */}
        <div className="request-section">
          <div className="request-section-header">Даты</div>
          <div className="request-section-card">
            <InfoRow label="Создана" value={formatDate(request.createdAt)} icon="📅" />
            <InfoRow label="Обновлена" value={formatDate(request.updatedAt)} icon="🔄" />
            {request.resolutionTargetAt && (
              <InfoRow label="Срок решения" value={formatDate(request.resolutionTargetAt)} icon="⏰" />
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="request-actions">
        <button className="request-action-btn request-action-btn--primary" onClick={handleOpenInItsm}>
          <span>🔗</span>
          <span>Открыть в ITSM</span>
        </button>
        <button className="request-action-btn request-action-btn--secondary" onClick={handleViewHistory}>
          <span>📜</span>
          <span>История изменений</span>
        </button>
      </div>
    </div>
  );
}
